import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User,
  GoogleAuthProvider,
  GithubAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  UserCredential,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, firestore, storage } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { UserProfile, getUserProfile, createUserProfile } from '../utils/userService';

interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  logout: () => Promise<void>;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>;
  uploadProfileImage: (file: File, uid: string) => Promise<string>;
  requireDisplayNameMsg: string | null;
  setRequireDisplayNameMsg: React.Dispatch<React.SetStateAction<string | null>>;
}

export const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [requireDisplayNameMsg, setRequireDisplayNameMsg] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          let userProfile = await getUserProfile(firebaseUser.uid);
          if (!userProfile) {
            const newUserProfileData: Partial<UserProfile> = {
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
            };
            if (firebaseUser.displayName) newUserProfileData.displayName = firebaseUser.displayName;
            if (firebaseUser.photoURL) newUserProfileData.photoURL = firebaseUser.photoURL;

            await createUserProfile(firebaseUser.uid, newUserProfileData);
            userProfile = await getUserProfile(firebaseUser.uid);
          }
          setUser(userProfile);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error("Error during auth state change:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (user && !user.displayName) {
      setRequireDisplayNameMsg('Please set your display name to continue.');
      navigate('/profile');
    }
  }, [user, navigate]);

  const logout = async () => {
    await signOut(auth);
  };
  
  const updateUserProfile = async (updates: Partial<UserProfile>) => {
    if (user) {
      // Update profile in Firebase Auth (only for displayName and photoURL)
      const authUpdates: { displayName?: string; photoURL?: string } = {};
      if (updates.displayName) authUpdates.displayName = updates.displayName;
      if (updates.photoURL) authUpdates.photoURL = updates.photoURL;
      
      if (Object.keys(authUpdates).length > 0 && auth.currentUser) {
        await updateProfile(auth.currentUser, authUpdates);
      }
      
      // Update all data in Firestore
      await createUserProfile(user.uid, updates);
      
      // Update context state with the latest user object
      const updatedUser = await getUserProfile(user.uid);
      setUser(updatedUser);
    }
  };
  
  const uploadProfileImage = async (file: File, uid: string): Promise<string> => {
    const storageRef = ref(storage, `profile_images/${uid}/${file.name}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    await updateUserProfile({ photoURL: downloadURL });
    
    return downloadURL;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      logout,
      updateUserProfile,
      uploadProfileImage,
      requireDisplayNameMsg,
      setRequireDisplayNameMsg,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 